import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { ELSButton } from '@els/els-component-button-react';
import PageHeader from '../../components/page-header/PageHeader.component';
import { useHistory } from 'react-router';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { flowRight } from 'lodash';
import cx from 'classnames';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { RecommendationAttemptStatusDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RoutePath } from '../../components/app/app.constants';
import { addSearchParams } from '../../utilities/app.utilities';
import withRecommendationPlayer, { RecommendationPlayerProps } from '../../hocs/with-recommendation-player/withRecommendationPlayer.hoc';
import withRemediationBase from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import { RecordEmpty } from '../../models/common.models';
import CloseRecommendationPlayerModal, { CloseRecommendationPlayerModalId } from '../../components/close-recommendation-player-modal/CloseRecommendationPlayerModal.component';
import FocusTrap from 'focus-trap-react';
import {
  getRecommendationTitle,
  getTotalProgress
} from '../../utilities/remediation.utilities';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { AppContext } from '../../components/app/AppProvider.component';
import { getExternalAppRedirectUrlAction } from '../../components/app/app-state-actions.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import { SherpathAIChatToggle } from '../../components/sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import {
  PageContextType,
  buildSherpathAIPageContext
} from '../../utilities/sherpath-ai-context.utilities';
import { buildTopicOverviewPageContext } from '../../utilities/sherpath-ai-page-context-builders.utilities';

export type TopicOverviewPlayerProps = RecommendationPlayerProps
  & ELSPropsFromModalService
  & ELSPropsFromToastService;

export const TopicOverviewPlayerPage = (props: TopicOverviewPlayerProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const [redirectUrl, setRedirectUrl] = useState<string>(null);

  const history = useHistory();

  const {
    recommendation,
    recommendationAttempt
  } = props;

  useEffect(() => {
    const contentId = recommendation?.contentId;

    // The player is not remounted when the recommendation changes, so drop the previous
    // topic's URL and ignore any response that arrives after this effect is cleaned up.
    setRedirectUrl(null);

    if (!contentId) {
      return undefined;
    }

    let isCurrent = true;

    getExternalAppRedirectUrlAction({
      appState,
      appStateRef,
      setAppState,
      config: {
        action: AppAction.ASSESSMENT_START,
        app: Application.EHE_MEDICAL_ASSESSMENT,
        body: {
          targetCapability: 'topic-overview-preview',
          contentId
        },
        includeLinkHash: true,
        altSrcApp: Application.REMEDIATION
      }
    }).then((response) => {
      if (isCurrent && response) {
        setRedirectUrl(response.redirectUrl);
      }
    }).catch(() => {
      // handleRequestError has already tracked the failure; no custom error UI by design.
    });

    return () => {
      isCurrent = false;
    };
  }, [recommendation?.contentId]);

  if (!recommendation || (!recommendationAttempt && props.referer === RoutePath.REMEDIATION_STUDENT_HOME)) {
    return null;
  }

  // Build Topic Overview-specific page context when data is available
  const getPageContext = () => {
    if (!appState.remediationBaseState.remediationAssignmentWithSources) {
      return null;
    }

    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }

    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: appState.remediationBaseState.remediationAssignmentWithSources,
      assessmentMetrics: appState.remediationBaseState.assessmentMetrics,
      assessmentSources: appState.remediationBaseState.raasAssessmentSourceList,
      hesiExamInfo: appState.remediationBaseState.hesiExamInfo,
      totalProgressPercent: getTotalProgress(appState.remediationBaseState),
      pageType: PageContextType.TOPIC_OVERVIEW,
      pageSpecificData: buildTopicOverviewPageContext({
        recommendation
      })
    });
  };

  const showCompleteButton = () => {
    return props.referer === RoutePath.REMEDIATION_STUDENT_HOME
      && props.recommendationAttempt
      && props.recommendationAttempt.status !== RecommendationAttemptStatusDto.COMPLETED_PASSED;
  };

  const handleReturnNavigation = () => {
    if (props.referer === RoutePath.REMEDIATION_STUDENT_HOME) {
      return history.push(addSearchParams(RoutePath.REMEDIATION_POST_PLAYER, {
        recommendationAttemptId: props.recommendationAttempt.id
      }));
    }

    if (props.referer === RoutePath.REMEDIATION_STUDENT_DETAIL) {
      return history.push(RoutePath.REMEDIATION_STUDENT_DETAIL);
    }

    return null;
  };

  const openPlayerModal = (isForSubmission: boolean, onConfirm: () => void) => {
    props.modalService.openCustomModal({
      modalId: CloseRecommendationPlayerModalId,
      noFocusTrap: true,
      modal: (
        <FocusTrap>
          <CloseRecommendationPlayerModal
              modalService={props.modalService}
              recommendationId={recommendation.id}
              recommendationFeedback={props.recommendationFeedback}
              recommendationContentType={recommendation.contentType}
              isForSubmission={isForSubmission}
              onConfirm={onConfirm}
          />
        </FocusTrap>
      ),
    });
  };

  const handleOpenCompleteModal = () => {
    openPlayerModal(true, () => {
      props.handleSubmitAttempt(0).then(() => {
        handleReturnNavigation();
      });
    });
  };

  return (
    <div className="c-scm-topic-overview-player">
      <header className="c-scm-topic-overview-player__header">
        <PageHeader
          title="Topic Overview"
          onCloseClick={() => openPlayerModal(false, handleReturnNavigation)}
        >
          <SherpathAIChatToggle
            remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
            pageContextType={PageContextType.TOPIC_OVERVIEW}
          />
        </PageHeader>
      </header>

      <div className="c-scm-topic-overview-player__main">
        <div className={cx('c-scm-topic-overview-player__content', {
          'c-scm-topic-overview-player__content--chat-active': appState.isChatActive
        })}>
          <FlexLayout classes={['c-scm-topic-overview-player__body']}>
            <FlexItem modifiers={[FlexLayoutModifier.GROW]} classes={['c-scm-topic-overview-player__body-content']}>
              <div className="c-scm-topic-overview-player__iframe-loading">
                Loading Topic Overview. Please allow a moment...
              </div>
              {redirectUrl && (
                <iframe title="topic-overview-iframe"
                        src={redirectUrl}
                        className="c-scm-topic-overview-player__iframe"
                        allowFullScreen
                        allow="clipboard-read; clipboard-write" />
              )}
            </FlexItem>
            <SherpathAIChatSidebar
              remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
              pageContext={getPageContext()}
              pageContextType={PageContextType.TOPIC_OVERVIEW}
            />
          </FlexLayout>
          <div className="c-scm-topic-overview-player__footer">
            <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.MIDDLE]}>
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <h4>{getRecommendationTitle(recommendation)}</h4>
              </FlexItem>
              <FlexItem isRender={showCompleteButton()}>
                <ELSButton
                  onClick={handleOpenCompleteModal}
                  size="small"
                >
                  Complete topic overview
                </ELSButton>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

const updateProgressState = false;

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Topic Overview Player' }),
  ELSWithToastService,
  ELSWithModalService,
  withRemediationBase(updateProgressState),
  withRecommendationPlayer,
);

const TopicOverviewPlayer = enhance(TopicOverviewPlayerPage) as unknown as React.FC<RecordEmpty>;

export default TopicOverviewPlayer;
